import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit3, Trash2, Save, X, Calendar, 
  Car, Shield
} from 'lucide-react';
import api from '../../services/api';
import { formatDate, parseLocalDate, toLocalISODate } from '../../utils/dateUtils';
import toast from '../../utils/toast';
import ConfirmModal from '../UI/ConfirmModal';
import { StatusBadge } from '../UI/StatusBadge';
import { InfoBadge } from '../UI/InfoBadge';
import DatePickerField from '../UI/DatePickerField';
import { maskValue, SensitiveRevealButton } from '../UI/SensitiveFieldDisplay';

const DrivingLicenseManager = ({ staffId, isEditing = false, readOnly = false, employmentPassType = null, onCountChange = null }) => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [formData, setFormData] = useState({
    license_class: '',
    issue_date: '',
    expiry_date: '',
    is_lifetime: false,
    license_number: '',
    verification_status: 'pending',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [sensitiveFieldsVisible, setSensitiveFieldsVisible] = useState({});

  // Singapore driving license classes with badge-friendly descriptions
  const licenseClasses = [
    { value: '2b', label: 'Class 2B', badge: 'Motorcycle ≤200cc', badgeColor: 'blue' },
    { value: '2a', label: 'Class 2A', badge: 'Motorcycle ≤400cc', badgeColor: 'blue' },
    { value: '2', label: 'Class 2', badge: 'Motorcycle >400cc', badgeColor: 'blue' },
    { value: '3', label: 'Class 3', badge: 'Car', badgeColor: 'green' },
    { value: '3a', label: 'Class 3A', badge: 'Auto Car', badgeColor: 'green' },
    { value: '3c', label: 'Class 3C', badge: 'Light Goods', badgeColor: 'orange' },
    { value: '4', label: 'Class 4', badge: 'Heavy Goods', badgeColor: 'purple' },
    { value: '4a', label: 'Class 4A', badge: 'Heavy Manual', badgeColor: 'purple' },
    { value: '5', label: 'Class 5', badge: 'Heavy Vehicle', badgeColor: 'indigo' },
    { value: '5a', label: 'Class 5A', badge: 'Heavy Manual', badgeColor: 'indigo' }
  ];

  const verificationStatuses = [
    { value: 'pending', label: 'Pending Verification' },
    { value: 'verified', label: 'Verified' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'expired', label: 'Expired' }
  ];

  useEffect(() => {
    if (staffId) {
      fetchLicenses();
    }
  }, [staffId]);

  const fetchLicenses = async () => {
    try {
      setLoading(true);
      const response = await api.getDrivingLicenses(staffId);
      const fetched = response.data?.results || response.data || [];
      setLicenses(fetched);
      if (onCountChange) onCountChange(fetched.length);
    } catch (error) {
      console.error('Error fetching driving licenses:', error);
      if (onCountChange) onCountChange(0);
      if (error.response?.status !== 404) {
        toast.error('Failed to load driving licences');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      license_class: '',
      issue_date: '',
      expiry_date: '',
      is_lifetime: false,
      license_number: '',
      verification_status: 'pending',
      notes: ''
    });
    setFormErrors({});
    setShowAddForm(false);
    setEditingLicense(null);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.license_class) {
      errors.license_class = 'Licence class is required';
    }
    if (!formData.issue_date) {
      errors.issue_date = 'Issue date is required';
    }
    
    // Only require expiry date for lifetime licenses if explicitly set
    // Some license types may not have expiry dates
    if (formData.is_lifetime && formData.expiry_date) {
      errors.expiry_date = 'Lifetime licences should not have expiry dates';
    }
    
    // Validate that expiry date is after issue date if both are provided
    if (formData.issue_date && formData.expiry_date && 
        new Date(formData.expiry_date) <= new Date(formData.issue_date)) {
      errors.expiry_date = 'Expiry date must be after issue date';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      const submitData = { ...formData, staff_id: staffId };
      
      if (editingLicense) {
        await api.updateDrivingLicense(editingLicense.id, submitData);
        toast.success('Driving licence updated successfully!');
      } else {
        await api.createDrivingLicense(submitData);
        toast.success('Driving licence added successfully!');
      }
      
      await fetchLicenses();
      resetForm();
    } catch (error) {
      console.error('Error saving driving license:', error);
      toast.error('Failed to save driving licence');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (license) => {
    setEditingLicense(license);
    setFormData({
      license_class: license.license_class || '',
      issue_date: license.issue_date || '',
      expiry_date: license.expiry_date || '',
      is_lifetime: license.is_lifetime || false,
      license_number: license.license_number || '',
      verification_status: license.verification_status || 'pending',
      notes: license.notes || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = (id) => {
    setConfirmAction({ id });
  };

  const handleConfirmDelete = async () => {
    if (!confirmAction) return;
    try {
      await api.deleteDrivingLicense(confirmAction.id);
      toast.success('Driving licence deleted successfully');
      await fetchLicenses();
    } catch (error) {
      console.error('Error deleting driving license:', error);
      toast.error('Failed to delete driving licence');
    } finally {
      setConfirmAction(null);
    }
  };

  const toggleFieldVisibility = (fieldName) => {
    setSensitiveFieldsVisible(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const maskSensitiveData = (value, fieldName) => {
    if (!value) return 'Not provided';
    if (sensitiveFieldsVisible[fieldName]) return value;
    return maskValue(value, 'account');
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const isExpiringSoon = (expiryDate, days = 90) => {
    if (!expiryDate) return false;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= days && daysUntilExpiry > 0;
  };

  const getLicenseClassDisplay = (classValue) => {
    const licenseClass = licenseClasses.find(lc => lc.value === classValue);
    return licenseClass ? licenseClass.label : classValue;
  };

  // Filter available license classes based on employment pass type
  const getAvailableLicenseClasses = () => {
    // If no employment pass type provided, show all classes
    if (!employmentPassType) return licenseClasses;
    
    // Filter out Class 3C for citizens and PRs
    if (['citizen', 'pr'].includes(employmentPassType)) {
      return licenseClasses.filter(lc => lc.value !== '3c');
    }
    
    // Show all classes for work permit/S-Pass holders
    return licenseClasses;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 animate-pulse">
            <div className="flex justify-between items-start">
              <div className="flex-1 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="h-6 bg-gray-200 rounded w-24"></div>
                  <div className="h-5 bg-gray-200 rounded-full w-16"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                  <div className="h-4 bg-gray-200 rounded w-28"></div>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-gray-600">
            {readOnly ? 'Employee driving licence information' : 'Driving Licences'}
          </p>
        </div>
        {!readOnly && !showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="btn btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            {readOnly ? 'View Licences' : 'Add Licence'}
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && !readOnly && (
        <div className="bg-gray-50 rounded-xl p-6 relative z-10">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            {editingLicense ? 'Edit Driving Licence' : 'Add Driving Licence'}
          </h4>
          
          {/* Class 3C Restriction Notice */}
          {employmentPassType && ['citizen', 'pr'].includes(employmentPassType) && (
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-800">
                <strong>Note:</strong> Class 3C (Light Goods Vehicle) licences are restricted to work permit and S-Pass holders only. 
                Singapore citizens and PRs cannot hold Class 3C licences.
              </p>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="form-label">Licence Class *</label>
              <select
                name="license_class"
                value={formData.license_class}
                onChange={handleInputChange}
                className={`select px-4 rounded-lg ${formErrors.license_class ? 'input-error' : ''}`}
              >
                <option value="">Select licence class</option>
                {getAvailableLicenseClasses().map(lc => (
                  <option key={lc.value} value={lc.value}>
                    {lc.label} - {lc.badge}
                  </option>
                ))}
              </select>
              {formErrors.license_class && (
                <p className="form-error">{formErrors.license_class}</p>
              )}
            </div>

            <div>
              <label className="form-label">Issue Date *</label>
              <DatePickerField
                theme="employee"
                selected={parseLocalDate(formData.issue_date)}
                onChange={(date) => {
                  const val = date ? toLocalISODate(date) : '';
                  setFormData(prev => ({ ...prev, issue_date: val }));
                  if (formErrors.issue_date) setFormErrors(prev => ({ ...prev, issue_date: null }));
                }}
                placeholderText="Select issue date"
              />
              {formErrors.issue_date && (
                <p className="form-error">{formErrors.issue_date}</p>
              )}
            </div>

            <div>
              <label className="form-label">Expiry Date</label>
              <DatePickerField
                theme="employee"
                selected={parseLocalDate(formData.expiry_date)}
                onChange={(date) => {
                  const val = date ? toLocalISODate(date) : '';
                  setFormData(prev => ({ ...prev, expiry_date: val }));
                  if (formErrors.expiry_date) setFormErrors(prev => ({ ...prev, expiry_date: null }));
                }}
                placeholderText="Select expiry date"
                disabled={formData.is_lifetime}
                isClearable
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.is_lifetime 
                  ? 'Lifetime licences do not expire' 
                  : 'Some licence types may not have expiry dates. Leave blank if not applicable.'
                }
              </p>
              {formErrors.expiry_date && (
                <p className="form-error">{formErrors.expiry_date}</p>
              )}
            </div>

            <div>
              <label className="form-label">Licence Number (Optional)</label>
              <input
                type="text"
                name="license_number"
                value={formData.license_number}
                onChange={handleInputChange}
                className="input px-4 rounded-lg"
                placeholder="Enter licence number"
              />
            </div>

            <div>
              <label className="form-label">Verification Status</label>
              <select
                name="verification_status"
                value={formData.verification_status}
                onChange={handleInputChange}
                className="select px-4 rounded-lg"
              >
                {verificationStatuses.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-3">
              <label className="form-label">Notes (Optional)</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={2}
                className="textarea px-4 py-3 rounded-lg"
                placeholder="Any additional notes about this licence"
              />
            </div>

            <div className="md:col-span-3">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="is_lifetime"
                  checked={formData.is_lifetime}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus-visible:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  This is a lifetime licence (no expiry date)
                </span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={resetForm}
              className="btn btn-outline"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary"
            >
              {saving ? (
                <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {saving ? 'Saving...' : (editingLicense ? 'Update Licence' : 'Add Licence')}
            </button>
          </div>
        </div>
      )}

      {/* Licenses List */}
      <div className="space-y-4 relative z-0">
        {licenses.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <Car className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No driving licences added</h3>
            <p className="text-gray-600">
              {readOnly 
                ? 'This employee has not added any driving licences yet. You can add licences on their behalf.' 
                : 'Add your Singapore driving licences for roles requiring driving'
              }
            </p>
          </div>
        ) : (
          licenses.map((license) => {
            const expired = isExpired(license.expiry_date);
            const expiringSoon = isExpiringSoon(license.expiry_date);
            return (
              <div key={license.id} className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        <h4 className="text-lg font-semibold text-gray-900">
                          {getLicenseClassDisplay(license.license_class)}
                        </h4>
                        {(() => {
                          const licenseClass = licenseClasses.find(lc => lc.value === license.license_class);
                          if (!licenseClass) return null;
                          return <InfoBadge color={licenseClass.badgeColor} label={licenseClass.badge} />;
                        })()}
                      </div>
                      
                      {/* Verification Status Badge */}
                      <StatusBadge
                        status={license.verification_status === 'verified' ? 'verified' : license.verification_status === 'rejected' ? 'rejected' : 'pending'}
                        label={verificationStatuses.find(s => s.value === license.verification_status)?.label || license.verification_status}
                      />

                      {/* Expiry Warnings */}
                      {license.expiry_date && expired && (
                        <StatusBadge status="rejected" label="Expired" showIcon />
                      )}
                      {license.expiry_date && !expired && expiringSoon && (
                        <StatusBadge status="pending" label="Expiring Soon" showIcon />
                      )}
                      {license.is_lifetime && (
                        <StatusBadge status="active" label="Lifetime" showIcon />
                      )}
                      {!license.expiry_date && !license.is_lifetime && (
                        <StatusBadge status="draft" label="No Expiry" showIcon />
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Calendar className="w-4 h-4 text-green-500" />
                        <span>Issued: {formatDate(license.issue_date)}</span>
                      </div>
                      
                      {license.expiry_date && (
                        <div className={`flex items-center gap-2 text-sm ${
                          expired ? 'text-red-600' : expiringSoon ? 'text-yellow-600' : 'text-gray-700'
                        }`}>
                          <Calendar className="w-4 h-4" />
                          <span>Expires: {formatDate(license.expiry_date)}</span>
                        </div>
                      )}
                      {!license.expiry_date && !license.is_lifetime && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar className="w-4 h-4" />
                           <span>No expiry date</span>
                         </div>
                       )}

                      {license.license_number && (
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Shield className="w-4 h-4 text-blue-500" />
                          <span className="font-mono">
                            {maskSensitiveData(license.license_number, `license_${license.id}`)}
                          </span>
                          <SensitiveRevealButton
                            isVisible={!!sensitiveFieldsVisible[`license_${license.id}`]}
                            onClick={() => toggleFieldVisibility(`license_${license.id}`)}
                            theme="employee"
                            placement="inline"
                            titleShow="Show licence number"
                            titleHide="Hide licence number"
                          />
                        </div>
                      )}
                    </div>

                    {license.notes && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700">{license.notes}</p>
                      </div>
                    )}
                  </div>

                  {!readOnly && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(license)}
                        className="icon-button"
                        title="Edit licence"
                        aria-label="Edit driving licence"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(license.id)}
                        className="icon-button-danger"
                        title="Delete licence"
                        aria-label="Delete driving licence"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <ConfirmModal
        isOpen={!!confirmAction}
        title="Delete Driving Licence"
        message="Are you sure you want to delete this driving licence?"
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmAction(null)}
        theme="admin"
      />
    </div>
  );
};

export default DrivingLicenseManager; 